import React, { useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useQueryClient } from '@tanstack/react-query';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useGenerateAuditConfig } from 'hooks/reports/useGenerateAuditConfig';
import { useGetAuditConfig } from 'hooks/reports/useGetAuditConfig';
import { showToast, TOAST_DEFAULT, FIVE_SECONDS, prefixClass } from 'lib/utils';
import KSButton from 'presentation/components/KSButton';
import Loading from 'components/Loading';
import { LocalErrorBoundary } from 'components/ErrorBoundary';
import { IconViewData, IconRefresh } from 'components/Icons';
import './BookkeeperBSAudit.scss';

const TOAST_GENERATE_AUDIT_CONFIG_TEXT = 'Generating audit configuration, will take a moment to complete';

function BookkeeperBSAudit(): JSX.Element {
  const queryClient = useQueryClient();
  const { status: locationsStatus, currentLocationId, currentLocation } = useLocationsStore();
  const { mutate: generateAuditConfig, isPending: isGenerating } = useGenerateAuditConfig();
  const {
    data: auditConfig,
    isLoading: isLoadingConfig,
    isFetching: isFetchingConfig,
    refetch: refetchConfig,
  } = useGetAuditConfig(currentLocationId || undefined, true);

  const isLoadingOrFetching = isLoadingConfig || isFetchingConfig;

  const handleGenerateAuditConfig = async () => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="alert-ui">
            <h3 className="alert">Generate Balance Sheet Audit Configuration</h3>
            <h4 className="text-center">
              This will generate the audit configuration for the current location's Balance Sheet reports.
            </h4>
            <div className="button-wrapper">
              <KSButton
                onClick={onClose}
                label="Cancel"
                textAlign="center"
                size="medium"
                color="secondary"
                className="mr-2"
                type="button"
              />
              <KSButton
                onClick={() => {
                  onClose();
                  showToast(TOAST_GENERATE_AUDIT_CONFIG_TEXT, TOAST_DEFAULT, FIVE_SECONDS);
                  if (currentLocationId) {
                    generateAuditConfig(
                      { locationId: currentLocationId },
                      {
                        onSuccess: data => {
                          // Update the query cache with the response data
                          queryClient.setQueryData(['auditConfig', currentLocationId], data);
                        },
                      }
                    );
                  }
                }}
                label="Generate Configuration"
                textAlign="center"
                size="medium"
                color="primary"
                type="button"
              />
            </div>
          </div>
        );
      },
    });
  };

  if (locationsStatus === 'loading') {
    return (
      <div className="bookkeeper-tab-container">
        <Loading unmount={false} />
      </div>
    );
  }

  const prefix = prefixClass('bookkeeper-bs-audit');

  const printLimit = (limit: number | null) => (limit !== null ? limit.toLocaleString() : '(no limit)');

  return (
    <div className={prefix()}>
      <div className={prefix('container')}>
        {(currentLocation?.id || currentLocationId === '') && (
          <div className={prefix('container-content')}>
            {/* Header Section with Actions */}
            <div className={prefix('container-header')}>
              <div className={prefix('container-header-content')}>
                <h2 className={prefix('container-header-title')}>Balance Sheet Audit Configuration</h2>
                <div className={prefix('container-action-buttons')}>
                  <KSButton
                    onClick={handleGenerateAuditConfig}
                    label={isGenerating ? 'Generating...' : 'Generate Audit Config'}
                    textAlign="center"
                    size="medium"
                    color="primary"
                    className="audit-config-btn"
                    type="button"
                    disabled={isGenerating || !currentLocationId}
                    icon={<IconViewData />}
                  />
                  <KSButton
                    onClick={() => {
                      refetchConfig();
                    }}
                    label={isLoadingOrFetching ? 'Loading...' : 'Refresh Rules'}
                    textAlign="center"
                    size="medium"
                    color="secondary"
                    className="view-rules-btn"
                    type="button"
                    disabled={isLoadingOrFetching || !currentLocationId}
                    icon={<IconRefresh />}
                  />
                </div>
              </div>
              <p className={prefix('container-header-description')}>
                Generate and manage audit configurations for Balance Sheet reports. This configuration defines the rules
                and thresholds used to validate Balance Sheet data integrity.
              </p>
            </div>

            {/* Information Section */}
            <div className={prefix('container-info-rules-wrapper')}>
              <div className={prefix('container-info-section')}>
                <div className={prefix('container-info-card')}>
                  <div className={prefix('container-info-card-header')}>
                    <IconViewData />
                    <h5 className={prefix('container-info-card-title')}>Current Status</h5>
                  </div>
                  <div className={prefix('container-info-card-content')}>
                    {currentLocation ? (
                      <>
                        <div className={prefix('container-info-card-row')}>
                          <span className={prefix('container-info-card-label')}>Location:</span>
                          <span className={prefix('container-info-card-value')}>{currentLocation.name}</span>
                        </div>
                        <div className={prefix('container-info-card-row')}>
                          <span className={prefix('container-info-card-label')}>Organization:</span>
                          <span className={prefix('container-info-card-value')}>{currentLocation.org}</span>
                        </div>
                        <div className={prefix('container-info-card-row')}>
                          <span className={prefix('container-info-card-label')}>QBO Environment:</span>
                          <span className={prefix('container-info-card-value')}>{currentLocation.qboEnv}</span>
                        </div>
                      </>
                    ) : (
                      <p>No location selected. Please select a location to manage audit configurations.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Audit Rules Section */}
              <div className={prefix('container-rules-section')}>
                <div className={prefix('container-rules-header')}>
                  <h4 className={prefix('container-rules-title')}>Audit Rules</h4>
                </div>
                {isLoadingOrFetching ? (
                  <Loading unmount={false} />
                ) : auditConfig &&
                  auditConfig.data &&
                  auditConfig.data.accounts &&
                  Object.keys(auditConfig.data.accounts).length > 0 ? (
                  <div className={prefix('container-rules-list')}>
                    {Object.entries(auditConfig.data.accounts).map(([accountId, rule]) => (
                      <div key={accountId} className={prefix('container-rules-item')}>
                        <div className={prefix('container-rules-item-header')}>
                          <div className={prefix('container-rules-item-title')}>
                            {accountId}: {rule.name}
                          </div>
                          <div className={prefix('container-rules-item-status enabled')}>{rule.type}</div>
                        </div>
                        <div className={prefix('container-rules-item-details')}>
                          {rule.low !== undefined && rule.high !== undefined && (
                            <span className={prefix('container-rules-item-detail')}>
                              <strong>Range:</strong> {printLimit(rule.low)} to {printLimit(rule.high)}
                            </span>
                          )}
                          {rule.method && (
                            <span className={prefix('container-rules-item-detail')}>
                              <strong>Method:</strong> {rule.method}
                            </span>
                          )}
                          {rule.decisionMetrics && (
                            <span className={prefix('container-rules-item-detail')}>
                              <strong>Metrics:</strong> R={rule.decisionMetrics.R.toFixed(2)}, n=
                              {rule.decisionMetrics.n}
                            </span>
                          )}
                          {rule.trendsNotAllowed && rule.trendsNotAllowed.length > 0 && (
                            <span className={prefix('container-rules-item-detail')}>
                              <strong>Trends Not Allowed:</strong> {rule.trendsNotAllowed.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={prefix('container-rules-empty')}>
                    <p>No audit rules found for this location.</p>
                    <p>Generate audit configuration to create rules.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const BookkeeperBSAuditWrapped = () => (
  <LocalErrorBoundary>
    <BookkeeperBSAudit />
  </LocalErrorBoundary>
);

export default BookkeeperBSAuditWrapped;
